'use client';
/***
 *
 * @file components/TextBox/index.tsx
 * @description TextBox component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Center
 * @since 13 Feb 2024
 *
 */

import React, { useEffect, useState } from 'react';
// import './InputField.scss';
import Label from '../Label';
import { StyledInput, StyledTextArea } from '@/shared/StyledComponents';
import { useFormContext } from '@/context/FormContextProvider';
import classNames from 'classnames';
import { T_KeyValue } from '@/types/app.type';
import { createKeyDownHandler } from '@/helpers/form.helper';
import { T_FormValidationCheckParams } from '@/types/form.type';
import ImageWrapper from '../ImageWrapper';
import { sendEventToGTM } from '@/helpers/gtm.helper';
import Cookies from 'js-cookie';
import { useSearchParams } from 'next/navigation';

/** type of props to be passed in InputField function component */
type T_Props = {
  /** id of Input Field content type */
  id?: string | undefined;
  /** css class of Input Field content type */
  className?: string;
  /** label color */
  labelColor?: string;
  /** label size */
  labelSize?: string;
  /** content of Input Field content type */
  data?: any;
  /** content type of Input Field content */
  typeid?: string;
  inputsRef?: React.MutableRefObject<Record<string, any | null | undefined>>;
  label?: string;
  inputSource?: "manual" | "cookie" | "query";
  inputKey?: string;
  type?: string;
  name?: string;
  customName?: string;
  defaultValue?: string | Boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: boolean;
  pattern?: string | RegExp;
  maxLength?: string | number;
  helperText?: string | undefined;
  formatErrorMessage?: string;
  confirmationErrorMessage?: string;
  requiredErrorMessage?: string;
  charLengthErrorMessage?: string;
  fieldToConfirm?: string;
  confirmationField?: string;
  checkedValue?: string;
  uncheckedValue?: string;
  children?: React.ReactNode
};

/**
 * Name: InputField
 *
 * Description: a functional component that can render different types of input fields along with their labels
 *
 * @param param0
 * @example parameter breakdown
 * ```
 *  param0: {
 *    id,
 *    data: {},    // contains the data required for rendering an input field inside of a label component
 *    typeid: "",  // used to denote the type of input field required
 *    inputsRef    // Reference to the current input field, used to update context
 *  }
 * ```
 * @example T_Props
 * ```
 * type T_Props = {
 *     id?: string,           // optional
 *     data?: any,            // optional
 *     typeid?: string,       // optional
 *     inputsRef?: React.MutableRefObject<Record<string, any | null | undefined>>;   // optional
 * }
 * ```
 * @returns a component containing an input field/ textArea along with its label
 */
const InputField = ({
  id = "input",
  className = "",
  data = {},
  typeid = "",
  defaultValue = "",
  inputSource = "manual",
  inputKey = "",
  inputsRef,
  labelColor = "",
  labelSize = "",
  maxLength = "255",
  pattern = "",
  helperText = "",
  formatErrorMessage = "",
  confirmationErrorMessage = "",
  requiredErrorMessage = "",
  charLengthErrorMessage = "",
  fieldToConfirm,
  confirmationField,
  checkedValue,
  uncheckedValue,
  children,
  // label, type, name, required, disabled, placeholder }: T_Props) => {
  ...props
}: T_Props) => {
  const { name: iName = "" } = props;
  const inputName = (iName === "custom" ? props.customName : iName) || "";
  const elData = data;
  const isInContentfulStudio = (typeof window !== "undefined") && window.self !== window.top;

  const isProbablyInEditorMode = isInContentfulStudio;

  const inputType: string = elData?.content?.type || props?.type;
  const inputRef = React.useRef<any | null | undefined>(null);
  const searchParams = useSearchParams();
  const { ctxFormData, updateCtxFormData, updateCtxFormSchema, focusedInput, setFocusedInput } = useFormContext();

  const [inputValue, setInputValue] = useState<string>((props?.type === "checkbox" || props?.type === "radio") ? Boolean(defaultValue) ? 'on' : '' : (String(defaultValue) || ''));
  const [inputErrorMessage, setInputErrorMessage] = useState<string | null>(elData?.content?.formatErrorMessage || formatErrorMessage);
  const [labelFontSize, setLabelFontSize] = useState<string>(`text-${labelSize}`);
  const [isValidInput, setIsValidInput] = useState<boolean>(true);
  const [confirmationError, setConfirmationError] = useState<T_KeyValue>({});
  const [host, setHost] = useState<string>("localhost:3000");
  const [origin, setOrigin] = useState<string>("https://localhost:3000");
  const [inputSchema, setInputSchema] = useState<T_KeyValue>({
    [inputName]: {
      required: props?.required,
      maxLength: maxLength,
      pattern: pattern,
      formatErrorMessage,
      confirmationErrorMessage,
      requiredErrorMessage,
      charLengthErrorMessage
    }
  })


  useEffect(() => {
    // if (inputRef?.current) {
    const formName = inputRef?.current?.form?.name;
    if (fieldToConfirm && confirmationField && inputName === confirmationField) {
      const error = ctxFormData?.[formName]?.data?.[fieldToConfirm || "email"] !== ctxFormData?.[formName]?.data?.[confirmationField || "confirmationEmail"];
      setConfirmationError({
        [inputName as string]: {
          error,
          message: elData?.content?.confirmationErrorMessage || confirmationErrorMessage
        }
      });
      updateCtxFormSchema({ formName, schema: { [inputName]: !error } })

    }
    // }
  }, [ctxFormData])

  useEffect(() => {
    // clearing ctxForm value so that errors are not shown when form is closed and reopened
    // Cookies.set("testCookie", "test-value", { expires: 7 });
    setHost(window?.location?.host);
    setOrigin(window?.location?.origin);
    const formname = inputRef?.current?.form?.name;
    return () => {
      updateCtxFormData({ formName: formname, name: inputName, value: "" });
    }
  }, [])

  // const ctxForm = ctxFormData?.[inputRef?.current?.form?.name]?.data;

  // debugger;
  const validationCheck = ({ target, ctxForm, cb = () => { } }: T_FormValidationCheckParams) => {
    const formName = target?.form?.name || "contactUs";
    let regex: RegExp;
    const val: string = target?.value;
    setConfirmationError({ [target?.name]: { error: false, message: elData?.content?.confirmationErrorMessage || confirmationErrorMessage } });
    // debugger
    if (pattern instanceof RegExp) {
      regex = pattern;
    } else if (val && typeof pattern === 'string' && pattern !== '') {
      // debugger;
      setIsValidInput(true);
      try {
        regex = new RegExp(pattern);
        // setInputErrorMessage('Pattern:' + pattern)
      } catch (err) {
        // setInputErrorMessage('Invalid regex pattern:' + pattern)
        console.warn('Invalid regex pattern:', pattern);
        regex = /.*/; // fallback: match everything
      }
    } else {
      regex = /.*/; // default pattern if none provided
    }
    // debugger;

    let validVal: boolean = regex?.test(val);

    // Format check
    if (!validVal) {
      setInputErrorMessage(elData?.content?.formatErrorMessage || formatErrorMessage);
    }

    // debugger;

    // Required check for input
    if (["checkbox", "radio"].includes(target?.type) && (props?.required || elData?.content?.required) && !(target as HTMLInputElement)?.checked) {
      validVal = false;
      setInputErrorMessage(elData?.content?.requiredErrorMessage || requiredErrorMessage);
    } else if (!["checkbox", "radio"].includes(target?.type) && (props?.required || elData?.content?.required) && (!val || (val && val === ""))) {
      validVal = false;
      setInputErrorMessage(elData?.content?.requiredErrorMessage || requiredErrorMessage);
    }

    // this message will not be visible to the user as the user cannot enter characters after the limit is reached
    // Char length check for input
    // if ((maxLength || elData?.content?.maxLength) && (val && parseInt(maxLength as string) < val?.length)) {
    //   validVal = false;
    //   setInputErrorMessage(elData?.content?.charLengthErrorMessage || charLengthErrorMessage);
    // }

    setIsValidInput(validVal);

    cb();

    // ************** Varification and confirmation ***************
    let confirmError = false;
    if (val) {
      if ((fieldToConfirm && confirmationField) && (ctxForm?.[fieldToConfirm] && ctxForm?.[confirmationField])) {
        const ftc = target?.name === fieldToConfirm ? val : ctxForm?.[fieldToConfirm];
        const cf = target?.name === confirmationField ? val : ctxForm?.[confirmationField];
        // if(ftc !== cf) {
        if (ftc !== cf && !["email", "phone"].includes(inputName)) {
          confirmError = true;
          setConfirmationError({
            [inputName]: {
              // ...confirmationError?.[target?.name],
              error: true,
              message: elData?.content?.confirmationErrorMessage || confirmationErrorMessage
            }
          });
        } else if (ftc === cf) {
          confirmError = false;
          setConfirmationError({
            [inputName]: {
              ...confirmationError?.[target?.name],
              error: false
            }
          });
          // const form= document?.getElementsByName(formName);
          // const emailField=form[0]?.querySelector('input[type="email"]');
          // const emailErrorField=

        }
      }
    }
    //in future ctxFormSchema will have following 
    //{name: {
    //   required: props?.required,
    //       maxLength: maxLength,
    //       pattern: pattern,
    //       formatErrorMessage,
    //       confirmationErrorMessage,
    //       requiredErrorMessage,
    //       charLengthErrorMessage,
    //        readyToSUbmit:true
    // }}

    updateCtxFormSchema({ formName, schema: { [target?.name]: validVal && !confirmError } })

    /* return boolean if current inputfield is valid*/
    // return (validVal && !confirmError);
  }

  // const handleInvalid = (e: React.InvalidEvent<HTMLInputElement | HTMLTextAreaElement>)=>{
  const handleInvalid = (e: React.InvalidEvent<any>) => {
    e.preventDefault();
    const target = e?.currentTarget;
    const formName = target?.form?.name || "contactUs";
    const ctxForm = ctxFormData[formName]?.data;
    validationCheck({ target, ctxForm });
  }

  const handleChange = (e: React.ChangeEvent<any>) => {
    const target = e?.currentTarget;
    const formName = target?.form?.name || "contactUs";
    // setConfirmationError({[target?.name]: { error: false, message: elData?.content?.confirmationErrorMessage || confirmationErrorMessage}});

    const ctxForm = ctxFormData[formName]?.data;

    // let regex: RegExp;
    const val: string = target?.value;

    // setInputErrorMessage(formName)
    // debugger;
    console.log("handleChange---", JSON.stringify(target?.checked), formName)

    if (target?.type === "checkbox" || target?.type === "radio") {
      setInputValue(target?.checked ? 'on' : '');
      validationCheck({
        target, ctxForm,
        // cb: () => {
        //     setInputValue(val);
        //     updateCtxFormData({ formName: formName, name: target?.name, value: val });
        //   }
      });
    } else { //if(props?.required || elData?.content?.required){
      // if (pattern instanceof RegExp) {
      //   regex = pattern;
      // } else if (target?.value && typeof pattern === 'string' && pattern !== '') {
      //   // debugger;
      //   setIsValidInput(true);
      //   try {
      //     regex = new RegExp(pattern);
      //     // setInputErrorMessage('Pattern:' + pattern)
      //   } catch (err) {
      //     // setInputErrorMessage('Invalid regex pattern:' + pattern)
      //     console.warn('Invalid regex pattern:', pattern);
      //     regex = /.*/; // fallback: match everything
      //   }
      // } else {
      //   regex = /.*/; // default pattern if none provided
      // }
      // // debugger;

      // let validVal: boolean = regex?.test(val);

      // // Format check
      // if(!validVal) {
      //   setInputErrorMessage(elData?.content?.formatErrorMessage || formatErrorMessage);
      // }

      // // Required check for input
      // if((props?.required || elData?.content?.required) && (!val || (val && val === ""))) {
      //   validVal = false;
      //   setInputErrorMessage(elData?.content?.requiredErrorMessage || requiredErrorMessage);
      // }

      // // Char length check for input
      // if((maxLength || elData?.content?.maxLength) && (val && parseInt(maxLength as string) < val?.length)) {
      //   validVal = false;
      //   setInputErrorMessage(elData?.content?.charLengthErrorMessage || charLengthErrorMessage);
      // }

      // setIsValidInput(validVal);
      validationCheck({
        target, ctxForm, cb:
          () => {
            setInputValue(val);
            updateCtxFormData({ formName: formName, name: target?.name, value: val });
          }
      });

      // // ************** Varification and confirmation ***************
      // if(val) {
      //   if((fieldToConfirm && confirmationField) && (ctxForm?.[fieldToConfirm] && ctxForm?.[confirmationField])) {
      //     const ftc = target?.name === fieldToConfirm ? val : ctxForm?.[fieldToConfirm];
      //     const cf = target?.name === confirmationField ? val : ctxForm?.[confirmationField];
      //     if(ftc !== cf) {
      //       setConfirmationError({[inputName]: { 
      //         // ...confirmationError?.[target?.name],
      //         error: true,
      //         message: elData?.content?.confirmationErrorMessage || confirmationErrorMessage
      //       }});
      //     } else if(ftc === cf) {
      //       setConfirmationError({[inputName]: { 
      //         ...confirmationError?.[target?.name],
      //         error: false
      //       }});
      //     }
      //   }
      // }
      console.log('ctxFormData --- ', ctxFormData);
    }
    // else {
    //   setInputValue(val);
    //   updateCtxFormData({formName: formName, name: target?.name, value: val});
    // }
  };

  const handleFocus = (e: React.FocusEvent<any>) => {
    const target = e?.currentTarget;
    setFocusedInput(elData?.content?.name);
    if (e.target.nodeName === 'TEXTAREA') {
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length,
      );
    }

    /* InputField GTM datalayer implementaion for interacting with the field*/

    // if(!sessionStorage?.getItem(`${target?.form?.id}_${target?.name}`)){
    const sessionFrmFlag = JSON.parse(sessionStorage?.getItem(`${target?.form?.id}_gtm_frm_flag`) || `{}`);


    if (sessionFrmFlag?.interactedField != target?.name) {

      const currentStep = sessionFrmFlag?.nextStep || 1;
      sendEventToGTM({
        event: 'form_field_entered',
        formId: target?.form?.id,
        formName: target?.form?.name,
        formField: target?.name,
        formStepCount: currentStep,
        pagePath: window.location.pathname,
        // location: 'modal',
        // section: 'hero_banner',
        type: 'form',
        subType: target?.form?.name
      },);
      sessionStorage?.setItem(`${target?.form?.id}_gtm_frm_flag`, JSON.stringify({ ...sessionFrmFlag, "nextStep": currentStep + 1, interactedField: target?.name || "unknown" }));

    }
  };

  const handleBlur = (e: React.ChangeEvent<any>) => {
    setFocusedInput(null);
    e.preventDefault();
    const target = e?.currentTarget;
    const formName = target?.form?.name || "contactUs";
    const ctxForm = ctxFormData[formName]?.data;
    validationCheck({ target, ctxForm });
  };

  const handleKeyDown = createKeyDownHandler({ ctxFormData, validationCheck })
  // (e: React.KeyboardEvent<HTMLTextAreaElement>)=>{
  //   if (e.key === 'Tab') {
  //     e.preventDefault();
  //     const target = e?.currentTarget;
  //     const formName = target?.form?.name || "contactUs";
  //     const ctxForm = ctxFormData[formName]?.data;
  //     validationCheck({target, ctxForm});
  //     const focusable = Array.from(
  //       document.querySelectorAll<HTMLElement>(
  //         'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
  //       )
  //     ).filter(el => !el.hasAttribute('disabled'));
  //     const index = focusable.indexOf(e.currentTarget as HTMLElement);
  //     const next = e.shiftKey ? focusable[index - 1] : focusable[index + 1];
  //     if (next) next.focus();
  //   }
  // }

  useEffect(() => {
    // setInputSchema({
    //   [inputName]: {
    //     required: props?.required,
    //     maxLength: maxLength,
    //     pattern: pattern,
    //     formatErrorMessage,
    //     confirmationErrorMessage,
    //     requiredErrorMessage,
    //     charLengthErrorMessage
    //   }
    // })
    updateCtxFormSchema({
      formName: inputRef?.current?.form?.name,
      schema: {
        [inputName]: {
          required: props?.required,
          maxLength: maxLength,
          pattern: pattern,
          formatErrorMessage,
          confirmationErrorMessage,
          requiredErrorMessage,
          charLengthErrorMessage
        }
      }
    })
  }, [])

  useEffect(() => {
    // setLabelFontColor(`text-[${labelColor}]`);
    setLabelFontSize(`text-${labelSize}`)
    setInputValue((props?.type === "checkbox" || props?.type === "radio") ? Boolean(defaultValue) ? 'on' : '' : (String(defaultValue) || ''));
  }, [labelColor, labelSize, props?.type, defaultValue])


  // Wrapping all types of inputs
  const Wrapper = ({ children }: { children: React.ReactNode }) => (
    <div
      className="form-group"
      data-testid="InputField"
    >
      {children}
      <p className="m-0 text-input-error-message">
        {elData?.content?.formatErrorMessage}
      </p>
    </div>
  );

  const getValue = ({
    inputSource,
    inputKey,
    inputValue
  }: {
    inputSource: string;
    inputKey: string;
    inputValue?: string
  }): string => {
    switch (inputSource) {
      case "cookie":
        const cookieValue = Cookies.get(inputKey);
        return cookieValue ?? "";
      case "query":
        const queryValue = searchParams.get(inputKey);
        return queryValue ?? "";
      case "manual":
        return inputValue ?? "";
      default:
        return "";
    }
  };

  const InputLabel = ({ children, className = "", htmlFor }: { children?: React.ReactNode, className?: string, htmlFor?: string }) => {
    return (
      (elData?.content?.label || props?.label) &&
      <>
        <Label
          // className={classNames('form-control-label ', '!flex !items-center !flex-row py-1 px-0 !gap-0.5')}
          // htmlFor={elData?.content?.id || elData?.content?.name || props?.label}
          // required={elData?.content?.required || props?.required}
          // disabled={elData?.content?.disabled || props?.disabled}
          htmlFor={htmlFor}
          className={classNames(className, labelFontSize)}
          style={{ color: labelColor, paddingLeft: "0px" }}
          required={elData?.content?.required}
          disabled={elData?.content?.disabled}
        >
          {children}
          {(elData?.content?.label || props?.label || '')}
          {(props?.required && !["checkbox", "radio"].includes(inputType)) && "*"}
        </Label>

        {elData?.content?.helperText && (
          <p className="m-0 text-helper">
            <i>
              <small>{elData?.content?.helperText}</small>
            </i>
          </p>
          // ) : (
          //   props?.label
        )}
      </>
    );
  };

  return (
    <>
      {/* {inputName} -- {props?.customName} */}
      {inputType === "hidden" ? (
        <>
          <input
            type={isProbablyInEditorMode ? "text" : "hidden"}
            id={elData?.content?.id || (inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
            name={(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
            value={getValue({ inputSource, inputKey, inputValue })}
            ref={inputRef}
            className='form-group'
            title={(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
          />
        </>
      ) : (
        <>
          <div
            className="form-group"
            data-testid="InputField"
          >
            {
              (() => {
                switch (inputType) {
                  case 'checkbox':
                    return (
                      <div className='form-control-label !flex !items-center justify-center !flex-row py-1 !px-0 !gap-0.5'>
                        <StyledInput
                          ref={inputRef}
                          type={inputType}
                          key={`label-${(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}`}
                          id={elData?.content?.id || (inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
                          name={`chk_${(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}`}
                          title={(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
                          // value={inputValue}
                          checked={
                            inputValue === 'on'
                              ? true
                              : false
                          }
                          value={
                            inputValue === 'on'
                              ? (checkedValue && checkedValue !== "" ? checkedValue : inputValue)
                              : (uncheckedValue && uncheckedValue !== "" ? uncheckedValue : inputValue)
                          }
                          required={elData?.content?.required || props?.required}
                          disabled={elData?.content?.disabled || props?.disabled}
                          className={classNames(
                            "!h-min-3 !w-min-3",
                            `form-control-${elData?.content?.type}`,
                            elData?.content?.cssClass,
                            className,
                            // Necessary to be able to style this element
                            // {'appearance-none': inputValue==='off'},
                            // 'bg-input-checkbox',
                            // 'bg-transparent',
                            'p-0',
                            // 'border-1 border-white border-solid rounded-2',
                            // 'accent-[bg-input-checkbox-accent]',
                            // 'accent-transparent',
                            // Size
                            '!w-input-width-checkbox',
                            '!h-input-height-checkbox',
                            // Padding
                            '!py-input-padding-y-checkbox !px-input-padding-x-checkbox',
                            // Border color
                            { '!border-input-checkbox-color': isValidInput || !confirmationError?.[inputName]?.error },
                            // Border
                            '!border-input-checkbox',
                            // Border radius
                            '!rounded-input-checkbox',
                            // Background color
                            // 'bg-input-default-bg'
                            'disabled:!bg-input-disabled-bg',
                            // Others
                            'disabled:cursor-not-allowed',
                            // Error state
                            {
                              '!border-input-error-border bp-form-error':
                                !isValidInput || confirmationError?.[inputName]?.error
                            }
                            // TODO: focus state
                            // TODO: checked state
                          )}
                          aria-invalid={!isValidInput}
                          data-error={!isValidInput}

                          style={{
                            marginRight: '10px',
                            minWidth: '24px',
                            ...(elData?.content?.customStyles || {}),
                          }}

                          // role={elData?.content?.type || "checkbox"}
                          onChange={handleChange}
                          // onClick={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          onInvalid={handleInvalid}
                          onKeyDown={handleKeyDown}
                        />
                        {/* <input type='hidden' id={elData?.content?.id || (inputName === "custom" ? props?.customName : elData?.content?.name || inputName)} name={(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)} value={inputValue || 'off'} /> */}
                        {/* {JSON.stringify(inputValue)} */}
                        {/* </InputLabel> */}
                        <InputLabel htmlFor={elData?.content?.id || elData?.content?.name || props?.customName || inputName} className='p-0' >{children}</InputLabel>
                      </div>
                    );
                  case 'radio':
                    return (
                      <div className='form-control-label !flex !items-center !flex-row py-1 px-0 !gap-0.5'>
                        <InputLabel htmlFor={elData?.content?.id} className='p-0 mr-[auto]' />
                        <StyledInput
                          ref={inputRef}
                          type={inputType}
                          key={`label-${(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}`}
                          className={classNames(
                            "radio2",
                            "w-3 h-3",
                            `form-control-${elData?.content?.type}`,
                            elData?.content?.cssClass,
                            className,
                            // Necessary to be able to style this element
                            // 'appearance-none',
                            // Size
                            '!w-input-width-radio !h-input-height-radio',
                            // Padding
                            '!py-input-padding-y-radio !px-input-padding-x-radio',
                            // Border color
                            { '!border-input-radio-border': isValidInput || !confirmationError?.[inputName]?.error },
                            // Border width
                            '!border-input-radio',
                            // Border radius
                            '!rounded-input-radio',
                            // Background color
                            'bg-input-default-bg disabled:!bg-input-disabled-bg',
                            // Others
                            'disabled:cursor-not-allowed',
                            //accent color
                            'accent-input-radio-accent',
                            // 'focus:outline-none focus:ring-2 focus:ring-black',
                            // Error state
                            {
                              '!border-input-error-border bp-form-error':
                                !isValidInput || confirmationError?.[inputName]?.error
                            }
                            // TODO: focus state
                            // TODO: selected state
                          )}
                          aria-invalid={!isValidInput}
                          data-error={!isValidInput}
                          style={{
                            marginRight: '10px',
                            ...(elData?.content?.customStyles || {}),
                          }}
                          id={elData?.content?.id}
                          name={(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
                          title={(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
                          // value={inputValue || 'on'}
                          // checked={
                          //   inputValue === 'on'
                          //     ? true
                          //     : false
                          // }
                          required={elData?.content?.required || props?.required}
                          disabled={elData?.content?.disabled || props?.disabled}
                          // role={elData?.content?.type || "checkbox"}
                          // onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          onInvalid={handleInvalid}
                          onKeyDown={handleKeyDown}
                        />
                      </div>
                    );
                  case 'multiline text':
                  case 'textarea':
                    return (
                      <>
                        <InputLabel htmlFor={elData?.content?.id || (inputName === "custom" ? props?.customName : elData?.content?.name || inputName)} />
                        {/* Input: */}
                        <StyledTextArea
                          autoFocus={focusedInput === elData?.content?.name ? true : false}
                          ref={inputRef}
                          key={`label-${(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}`}
                          className={classNames(
                            `form-control form-control-${elData?.content?.type}`,
                            elData?.content?.cssClass,
                            className,
                            // Size
                            '!w-input-width',
                            // Padding
                            '!py-input-padding-y !px-input-padding-x',
                            // Border color
                            // '!border-input-default-border autofill:!border-input-autofilled-border',
                            { '!border-input-default-border autofill:!border-input-autofilled-border': isValidInput || !confirmationError?.[inputName]?.error },
                            // Border width
                            '!border-input !border-b-input-b',
                            // Border radius
                            '!rounded-input',
                            // Text color
                            '!text-input-default-text disabled:!text-input-disabled-text autofill:!text-input-autofilled-text',
                            // Background color
                            'bg-input-default-bg disabled:!bg-input-disabled-bg autofill:!bg-input-autofilled-bg',
                            // Placeholder color
                            'placeholder:!text-input-default-placeholder disabled:placeholder:!text-input--disabled-placeholder',
                            // Text size
                            '!text-input',
                            // Font family
                            'font-input placeholder:font-input',
                            // Others
                            'text-ellipsis disabled:cursor-not-allowed',
                            // Error state
                            {
                              '!bg-input-error-bg !text-input-error-text placeholder:!text-input-error-placeholder !border-input-error-border bp-form-error':
                                // elData?.content?.formatErrorMessage,
                                !isValidInput || confirmationError?.[inputName]?.error
                            }
                          )}
                          aria-invalid={!isValidInput}
                          data-error={!isValidInput}
                          style={{ ...(elData?.content?.customStyles || {}) }}
                          id={elData?.content?.id || (inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
                          name={(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
                          title={(inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
                          placeholder={elData?.content?.placeholder || props?.placeholder}
                          required={elData?.content?.required || props?.required}
                          disabled={elData?.content?.disabled || props?.disabled}
                          // pattern={elData?.content?.pattern || props?.pattern}
                          // role="textbox"
                          value={inputValue}
                          maxLength={Number(maxLength)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          onInvalid={handleInvalid}
                          onKeyDown={handleKeyDown}
                          rows={5}
                          cols={30}
                        />
                      </>
                    );
                  default:
                    return (
                      <>
                        <InputLabel htmlFor={elData?.content?.id || (inputName === "custom" ? props?.customName : elData?.content?.name || inputName)} />
                        <StyledInput
                          autoFocus={focusedInput === elData?.content?.name ? true : false}
                          ref={inputRef}
                          type={inputType}
                          key={`label-${elData?.content?.name || name}`}
                          className={classNames(
                            `form-control form-control-${elData?.content?.type}`,
                            elData?.content?.cssClass,
                            className,
                            // Size
                            '!w-input-width',
                            // Padding
                            '!py-input-padding-y !px-input-padding-x',
                            // Border color
                            { '!border-input-default-border autofill:!border-input-autofilled-border': isValidInput || !confirmationError?.[inputName]?.error },
                            // {'border-error': !isValidInput || confirmationError?.[inputName]?.error},
                            // Border width
                            '!border-input !border-b-input-b',
                            // Border radius
                            '!rounded-input',
                            // Text color
                            '!text-input-default-text disabled:!text-input-disabled-text autofill:!text-input-autofilled-text',
                            // Background color
                            'bg-input-default-bg disabled:!bg-input-disabled-bg autofill:!bg-input-autofilled-bg',
                            // Placeholder color
                            'placeholder:!text-input-default-placeholder disabled:placeholder:!text-input--disabled-placeholder',
                            // Text size
                            '!text-input',
                            // Font family
                            'font-input placeholder:font-input',
                            // Others
                            'text-ellipsis disabled:cursor-not-allowed',
                            // Error state
                            {
                              '!bg-input-error-bg !text-input-error-text placeholder:!text-input-error-placeholder !border-input-error-border bp-form-error':
                                // elData?.content?.formatErrorMessage ,
                                !isValidInput || confirmationError?.[inputName]?.error
                            },
                          )}
                          aria-invalid={!isValidInput}
                          data-error={!isValidInput}
                          style={{ ...(elData?.content?.customStyles || {}) }}
                          id={elData?.content?.id || (inputName === "custom" ? props?.customName : elData?.content?.name || inputName)}
                          name={inputName}
                          title={inputName}
                          value={inputValue}
                          placeholder={elData?.content?.placeholder || props?.placeholder}
                          required={elData?.content?.required || props?.required}
                          disabled={elData?.content?.disabled || props?.disabled}
                          // role="textarea"
                          {...(() => {
                            if (elData?.content?.pattern || pattern) {
                              return { pattern: elData?.content?.pattern || pattern }
                            }
                            else {
                              return {}
                            }
                          })()}
                          maxLength={Number(maxLength)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          onKeyDown={handleKeyDown}
                          onInvalid={handleInvalid}
                        />
                      </>
                    );
                }
              })()
            }
            {helperText && <p className="flex leading-[133%]">
              <ImageWrapper
                className='mt-[.2rem]'
                src={`/information.svg`}
                alt="info"
                width={15}
                height={15}
                loader={({ src }) => src}
              />
              <span className='ml-[.3rem] text-text-default w-[90%]'>{helperText}</span></p>}
            {/* {inputErrorMessage} */}
            {/* Err: {JSON.stringify(isValidInput)} */}
            {(!isValidInput) &&
              <p className="m-0 text-input-error-message">
                {/* Err: <pre>{JSON.stringify(confirmationError, null, 2)}</pre> */}
                {inputErrorMessage}
                {/* {elData?.content?.formatErrorMessage || formatErrorMessage} */}
              </p>
            }
            {confirmationError?.[inputName]?.error &&
              <p className="m-0 text-input-error-message">
                {/* Err: <pre>{JSON.stringify(confirmationError, null, 2)}</pre> */}
                {/* {formatErrorMessage} */}
                {confirmationError?.[inputName]?.message}
              </p>
            }
            {/* {inputRef?.current?.form?.name} */}
            {/* <pre>{JSON.stringify(ctxFormData[inputRef?.current?.form?.name]?.data, null, 2)}</pre> */}

            {/* Err: <pre>{JSON.stringify(confirmationError, null, 2)}</pre> */}
            {/* {JSON.stringify(!isValidInput)} -- 
            {JSON.stringify(!["email", "phone"].includes(inputName))} -- 
            {JSON.stringify(confirmationError?.[inputName]?.error)} */}
            {/* {JSON.stringify(isValidInput)} */}
          </div>
        </>
      )}
    </>
  )
};

export default InputField;
